import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { DocumentEditor, FormDataStoreProvider, PrefillBanner } from '@mes-amis/super-form'
import '@mes-amis/super-form/styles.css'
import './style.scss'
import createChannel from '../../../../cables/cable'

// Compare two HLC strings of the form `<physical-ms>-<logical>-<actor>`.
// Returns true iff `incoming` represents a strictly newer write than `current`.
// Falsy `incoming` is never newer; missing `current` is always older. For
// malformed timestamps (e.g. legacy MD5 content_revision values from documents
// that pre-date CRDT metadata), fall back to inequality so we still accept
// updates that look different — better to over-apply than to silently drop a
// real broadcast.
const isFieldHLCNewer = (incoming, current) => {
  if (!incoming) return false
  if (!current) return true
  const parse = (rev) => {
    const m = /^(\d+)-(\d+)-(.+)$/.exec(rev)
    return m ? { physical: Number(m[1]), logical: Number(m[2]), actor: m[3] } : null
  }
  const a = parse(incoming)
  const b = parse(current)
  if (!a || !b) return incoming !== current
  if (a.physical !== b.physical) return a.physical > b.physical
  if (a.logical !== b.logical) return a.logical > b.logical
  return a.actor > b.actor
}

const CopyableClientId = ({ slug }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(slug).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [slug])

  if (!slug) return null

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-100 rounded border border-gray-200 hover:bg-gray-200 cursor-pointer"
      title="Copy Client ID"
    >
      {copied ? 'Copied!' : `id: ${slug}`}
    </button>
  )
}

const ReactDocumentEditor = (props) => {
  const {
    template, document: doc, client, urls, csrfToken, signedStreamName, readonly, fieldPermissions,
    title, clientName, clientSlug, programName, defaultPanelOpen,
    dateCompleted, reopened, minorChange, hasSignatureConfig, helpDocument,
    fieldPrefills, confirmAllEnabled
  } = props

  // Form data lives in the FormDataStoreProvider mounted below. The host
  // keeps three parallel refs alongside it:
  //   - initialFormData: seed for the provider on mount (frozen at first render)
  //   - latestFormDataRef: mirror of every field write, used by the autosave
  //     to compute changed fields without subscribing to the store
  //   - lastSavedDataRef: snapshot of what the server has acknowledged, used
  //     to skip ActionCable echoes and detect local unsaved changes
  // actionsRef receives setFormData/mergeFormData from the provider so the
  // broadcast handler can push server-side changes into the store without
  // round-tripping through React state.
  const initialFormData = useRef(doc?.super_form || {}).current
  const latestFormDataRef = useRef(doc?.super_form || {})
  const lastSavedDataRef = useRef(doc?.super_form || {})
  const lastRevisionRef = useRef(doc?.revision || null)
  // Per-field HLC the client has accepted (from initial render, save responses,
  // or applied broadcasts). The broadcast handler uses this to skip a field
  // whose incoming HLC is older than what we've already accepted — protecting
  // against late-arriving broadcasts whose overall revision is "newer" but
  // whose per-field data is stale relative to a more recent local save.
  const fieldMetaRef = useRef({ ...(doc?.super_form_meta || {}) })
  const actionsRef = useRef(null)
  const saveTimeoutRef = useRef(null)
  const dateCompletedRef = useRef(dateCompleted || '')
  const hlcCounterRef = useRef(0)
  const [superFormMeta, setSuperFormMeta] = useState(doc?.super_form_meta || {})
  const [errors, setErrors] = useState({})

  const performSave = useCallback(async () => {
    const data = latestFormDataRef.current
    const changedFields = {}
    for (const [key, value] of Object.entries(data)) {
      if (JSON.stringify(value) !== JSON.stringify(lastSavedDataRef.current[key])) {
        changedFields[key] = value
      }
    }
    if (Object.keys(changedFields).length === 0) return

    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { super_form: changedFields } })
      })

      if (response.ok || response.status === 204) {
        lastSavedDataRef.current = { ...lastSavedDataRef.current, ...changedFields }

        // Advance lastRevisionRef + per-field HLC from the save response so any
        // broadcast carrying stale values for the fields we just wrote (an
        // echo of an earlier render that hadn't seen this save yet) gets
        // skipped. Without this, lastRevisionRef only moves when a broadcast
        // lands, and field-level meta never tracks the local writes at all.
        try {
          const body = await response.json()
          if (body?.revision) {
            lastRevisionRef.current = body.revision
            const next = { ...fieldMetaRef.current }
            for (const key of Object.keys(changedFields)) next[key] = body.revision
            fieldMetaRef.current = next
          }
        } catch (_) {
          // Empty body (204 from older servers) — nothing to advance.
        }
      }

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Autosave failed:', error)
    }
  }, [urls.autosave, csrfToken])

  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => performSave(), 900)
  }, [performSave])

  const handleFieldChange = useCallback((field, value) => {
    latestFormDataRef.current = { ...latestFormDataRef.current, [field]: value }
    scheduleSave()
  }, [scheduleSave])

  useEffect(() => {
    if (!signedStreamName) return

    const subscription = createChannel(
      { channel: 'Turbo::StreamsChannel', signed_stream_name: signedStreamName },
      {
        received(data) {
          try {
            const message = typeof data === 'string' ? JSON.parse(data) : data
            if (message?.type !== 'document_update' || !message.document) return

            const serverDoc = message.document
            const serverRevision = serverDoc.revision || null

            // Skip echoes of our own saves
            if (serverRevision && serverRevision === lastRevisionRef.current) return

            const serverFormData = serverDoc.super_form
            if (!serverFormData || typeof serverFormData !== 'object') return

            const serverFieldMeta = (serverDoc.super_form_meta && typeof serverDoc.super_form_meta === 'object')
              ? serverDoc.super_form_meta
              : {}

            const patch = {}
            for (const [key, value] of Object.entries(serverFormData)) {
              const localUnsaved = JSON.stringify(latestFormDataRef.current[key]) !== JSON.stringify(lastSavedDataRef.current[key])
              if (localUnsaved) continue

              // Per-field HLC guard: if our locally-tracked HLC for this field
              // is already >= the broadcast's HLC for it, the broadcast carries
              // stale data for that field — skip. This is the key protection
              // against late broadcasts whose document revision moved forward
              // (because some other field was edited) but whose super_form
              // payload for this field reflects a pre-save snapshot.
              if (!isFieldHLCNewer(serverFieldMeta[key], fieldMetaRef.current[key])) continue

              patch[key] = value
              latestFormDataRef.current = { ...latestFormDataRef.current, [key]: value }
              fieldMetaRef.current = { ...fieldMetaRef.current, [key]: serverFieldMeta[key] || fieldMetaRef.current[key] }
            }
            lastRevisionRef.current = serverRevision
            // Only fields we actually accepted should advance lastSavedDataRef;
            // blindly merging serverFormData (the pre-fix behavior) corrupts
            // the "what the server has acknowledged" snapshot when we skipped
            // some keys for staleness.
            lastSavedDataRef.current = { ...lastSavedDataRef.current, ...patch }

            if (Object.keys(patch).length > 0) {
              actionsRef.current?.mergeFormData(patch)
            }

            // PARC state from the broadcast — pending set drops naturally when
            // `confirmed_prefill:<slug>` keys arrive in super_form_meta from
            // another tab.
            if (serverDoc.super_form_meta && typeof serverDoc.super_form_meta === 'object') {
              setSuperFormMeta((current) => ({ ...current, ...serverDoc.super_form_meta }))
            }
          } catch (error) {
            console.error('[ReactDocumentEditor] Failed to process broadcast:', error)
          }
        }
      }
    )

    return () => {
      if (subscription) subscription.unsubscribe()
    }
  }, [signedStreamName])

  const handleBlur = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    performSave()
  }, [performSave])

  const saveDateCompleted = useCallback(async (value) => {
    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { recorded_at: value || null } })
      })

      if (!response.ok && response.status === 401) {
        window.location.reload()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Date completed save failed:', error)
    }
  }, [urls.autosave, csrfToken])

  const handleFinish = useCallback(async (validatedData) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)

    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      // Force JSON content-negotiation.
      // Include the current `super_form_meta` so the server's CRDT merge sees
      // any PARC confirms that were still optimistic locally
      const response = await fetch(urls.finalize, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          document: {
            super_form: validatedData,
            super_form_meta: superFormMeta,
          }
        })
      })

      const data = await response.json().catch(() => ({}))

      if (response.ok) {
        window.location.href = data.redirect_url || urls.documents
        return
      }

      if (data.field_errors && typeof data.field_errors === 'object') {
        setErrors(data.field_errors)
      }
      if (Array.isArray(data.errors) && data.errors.length > 0) {
        console.warn(`[ReactDocumentEditor] Could not finish the document:\n\n${data.errors.join('\n')}`)
      } else {
        console.error('[ReactDocumentEditor] Could not finish the document. Please reload the page and try again.')
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Finalize failed:', error)
    }
  }, [urls.finalize, urls.documents, csrfToken, superFormMeta])

  const handleSaveAndExit = useCallback(async () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    await performSave()
    window.location.href = urls.documents
  }, [performSave, urls.documents])

  const handleBack = useCallback(() => {
    window.location.href = urls.documents
  }, [urls.documents])

  // PARC confirm path. Each confirm writes a `confirmed_prefill:<slug>`
  // HLC timestamp into `super_form_meta`. The server-side Crdt::Update
  // merges these per-key (server is authoritative), so concurrent confirms
  // from another admin tab converge cleanly via the cable broadcast.
  // Format matches the offline app's HLC: `${ms}-${counter}-${actor}`.
  const buildHlc = useCallback(() => {
    const ms = Date.now()
    const counter = hlcCounterRef.current++
    return `${ms}-${counter}-admin-${doc?.id ?? 'session'}`
  }, [doc?.id])

  const writeMeta = useCallback(async (metaPatch) => {
    const rollback = () => {
      // The server didn't persist the patch — drop the optimistic local keys
      // so the UI reflects truth.
      setSuperFormMeta((prev) => {
        const next = { ...prev }
        for (const key of Object.keys(metaPatch)) delete next[key]
        return next
      })
    }

    try {
      const tokenEl = window.document.querySelector('meta[name="csrf-token"]')
      const token = tokenEl ? tokenEl.content : csrfToken

      const response = await fetch(urls.autosave, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ document: { super_form_meta: metaPatch } })
      })

      if (response.status === 401) {
        window.location.reload()
        return
      }
      if (!response.ok) {
        console.warn('[ReactDocumentEditor] Prefill confirm not persisted (status', response.status, ')')
        rollback()
      }
    } catch (error) {
      console.error('[ReactDocumentEditor] Prefill confirm failed:', error)
      rollback()
    }
  }, [urls.autosave, csrfToken])

  const handlePrefillConfirmed = useCallback((slug) => {
    if (!slug) return
    const hlc = buildHlc()
    setSuperFormMeta((prev) => ({ ...prev, [`confirmed_prefill:${slug}`]: hlc }))
    void writeMeta({ [`confirmed_prefill:${slug}`]: hlc })
  }, [writeMeta, buildHlc])

  // Batch variant — one PATCH for N slugs. Used by the row dot in
  // FieldTables (a single click confirms every PARC-pending cell in the
  // row) and by the "Confirm section" / "Confirm all" modal flows.
  // Calling handlePrefillConfirmed in a loop would create N read-modify-write
  // races on the server side; one PATCH avoids that entirely.
  const handlePrefillsConfirmed = useCallback((slugs) => {
    if (!Array.isArray(slugs) || slugs.length === 0) return
    const patch = {}
    for (const slug of slugs) {
      patch[`confirmed_prefill:${slug}`] = buildHlc()
    }
    setSuperFormMeta((prev) => ({ ...prev, ...patch }))
    void writeMeta(patch)
  }, [writeMeta, buildHlc])

  // Set of slugs the user (or another tab) has confirmed, derived from
  // `super_form_meta` keys. Single source of truth for tint/dot clearing
  // — the package's PrefillProvider re-derives the pending PARC Array
  // internally from `prefills × confirmedPrefillsSet`.
  const confirmedPrefillsSet = useMemo(() => {
    const set = new Set()
    for (const key of Object.keys(superFormMeta)) {
      if (key.startsWith('confirmed_prefill:')) {
        set.add(key.slice('confirmed_prefill:'.length))
      }
    }
    return set
  }, [superFormMeta])

  const queryModelRecords = useCallback(async (model, opts) => {
    if (model === 'Contact' && urls.contacts && opts.clientId) {
      try {
        const response = await fetch(urls.contacts, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          return data.map((c) => ({
            id: c.id,
            label: c.display_name || `${c.first_name} ${c.last_name}`,
            data: c
          }))
        }
      } catch (error) {
        console.error('[ReactDocumentEditor] Failed to query model records:', error)
      }
    }
    return []
  }, [urls.contacts, csrfToken])

  const renderActions = useCallback(() => (
    urls.toggleReactSuperForm ? (
      <a
        href={urls.toggleReactSuperForm}
        data-turbo-method="post"
        className="text-sm text-gray-500 hover:text-gray-700 underline"
      >
        Switch to Classic Editor
      </a>
    ) : null
  ), [urls.toggleReactSuperForm])

  const renderTitleContent = useCallback(() => (
    <div className="flex flex-col">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">{clientName}</h1>
        <CopyableClientId slug={clientSlug} />
      </div>
      <span className="text-sm">
        <span className="text-gray-500">{title}</span>
        {programName && <span className="text-gray-400"> — {programName}</span>}
      </span>
    </div>
  ), [clientName, clientSlug, title, programName])

  const renderBeforeForm = useCallback(() => {
    const banners = []

    // PARC aggregate banner
    banners.push(<PrefillBanner key="parc-banner" />)

    if (reopened && hasSignatureConfig) {
      banners.push(
        <div key="reopened-sig" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            {minorChange ? (
              <span>This document is now re-opened for <strong>minor changes</strong>. The update won't require redoing signatures.</span>
            ) : (
              <span>This document is now <strong>re-opened</strong> for editing. After making the changes, re-sign it again. You can access the previous version in the document version history.</span>
            )}
          </div>
        </div>
      )
    } else if (reopened) {
      banners.push(
        <div key="reopened" className="p-4 bg-gray-100 rounded-md mb-4">
          <div className="flex items-center">
            <span className="mr-2">&#9888;</span>
            <span>This document is now <strong>re-opened</strong> for editing. You can access the previous version in the document version history.</span>
          </div>
        </div>
      )
    }

    return banners.length > 0 ? <>{banners}</> : null
  }, [readonly, reopened, minorChange, hasSignatureConfig])

  return (
    <div className="react-super-form-container super-form" data-testid="react-document-editor">
      <FormDataStoreProvider
        initialFormData={initialFormData}
        actionsRef={actionsRef}
        onChange={handleFieldChange}
      >
        <DocumentEditor
          template={template}
          document={doc}
          helpDocument={helpDocument || null}
          onBlur={handleBlur}
          onFinish={handleFinish}
          onSaveAndExit={handleSaveAndExit}
          onBack={handleBack}
          renderTitleContent={renderTitleContent}
          defaultPanelOpen={defaultPanelOpen}
          renderBeforeForm={renderBeforeForm}
          renderActions={renderActions}
          client={client}
          readonly={readonly}
          errors={errors}
          fieldPermissions={fieldPermissions}
          queryModelRecords={queryModelRecords}
          showSummary={template.show_summary !== false && (
            !!template.super_form?.definition?.summary ||
            (template.super_form?.definition?.interrai?.algorithms?.length ?? 0) > 0
          )}
          prefills={fieldPrefills}
          confirmedPrefillsSet={confirmedPrefillsSet}
          confirmAllEnabled={confirmAllEnabled}
          clientName={clientName}
          onPrefillConfirmed={handlePrefillConfirmed}
          onPrefillsConfirmed={handlePrefillsConfirmed}
          showDateCompleted={!!dateCompleted}
          recordedAt={dateCompleted}
          onRecordedAtChange={saveDateCompleted}
          saveAndExitLabel="Save and Exit"
          finishLabel="Finish Document"
          saveAndExitBtnClassName="react-sf-btn react-sf-btn--secondary"
          finishBtnClassName="react-sf-btn react-sf-btn--primary"
        />
      </FormDataStoreProvider>
    </div>
  )
}

export default ReactDocumentEditor
